import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Typography,
  Collapse,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { KeyboardArrowDown as ExpandMoreIcon, KeyboardArrowUp as ExpandLessIcon, Warning as WarningIcon } from '@mui/icons-material';
import inventoryReservationService, { ActiveReservationsResponse, AggregatedReservation } from '../../services/inventoryReservationService';
import EmptyState from './EmptyState';
import { formatQty } from '../../utils/quantityFormat';

interface ReservedItemsCardProps {
  venueId: string;
}

export const ReservedItemsCard: React.FC<ReservedItemsCardProps> = ({ venueId }) => {
  const theme = useTheme();
  const [data, setData] = useState<ActiveReservationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const result = await inventoryReservationService.getActiveReservations(venueId);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load reservations');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [venueId]);

  const toggleExpand = (itemId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedRows(newExpanded);
  };

  const getSeverityColor = (severity: string) => {
    if (severity === 'red') return theme.palette.error.main;
    if (severity === 'amber') return theme.palette.warning.main;
    return 'inherit';
  };

  const formatAvailable = (qty: number) => {
    if (qty < 0) {
      return (
        <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600 }}>
          Oversold by {formatQty(Math.abs(qty))}
        </Typography>
      );
    }
    return <Typography variant="body2">{formatQty(qty)}</Typography>;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 2 }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    );
  }

  if (!data || data.totalItemsReserved === 0) {
    return <EmptyState icon={WarningIcon} message="Nothing reserved right now." />;
  }

  return (
    <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
      {/* Summary line */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`, bgcolor: alpha(theme.palette.info.main, 0.02) }}>
        <Typography variant="body2" color="text.secondary">
          {data.totalItemsReserved} item{data.totalItemsReserved !== 1 ? 's' : ''} reserved
          {data.totalHoldsStale > 0 && (
            <Typography component="span" variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 600, ml: 1 }}>
              · {data.totalHoldsStale} with holds over 24h
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Item</TableCell>
              <TableCell align="right">Reserved</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="center"># Orders</TableCell>
              <TableCell align="center"># Holds</TableCell>
              <TableCell>Locations</TableCell>
              <TableCell>Oldest hold</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((item) => (
              <React.Fragment key={item.itemId}>
                {/* Rollup row */}
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.divider, 0.02),
                    '&:hover': { bgcolor: alpha(theme.palette.divider, 0.05) },
                  }}
                >
                  <TableCell sx={{ width: 40 }}>
                    <IconButton size="small" onClick={() => toggleExpand(item.itemId)}>
                      {expandedRows.has(item.itemId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.itemName}
                    </Typography>
                    {item.itemSku && (
                      <Typography variant="caption" color="text.secondary">
                        {item.itemSku}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{formatQty(item.reservedQty)}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatAvailable(item.totalAvailable)}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{item.orderCount}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{item.holdCount}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {item.locations.join(', ')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getSeverityColor(item.oldestHoldAge.severity),
                        fontWeight: item.oldestHoldAge.severity === 'red' ? 600 : 500,
                      }}
                    >
                      {item.oldestHoldAge.formatted}
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Expand detail rows */}
                <TableRow>
                  <TableCell colSpan={8} sx={{ p: 0 }}>
                    <Collapse in={expandedRows.has(item.itemId)} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: alpha(theme.palette.divider, 0.02) }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                          Holds ({item.holds.length})
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>POS ref</TableCell>
                              <TableCell>Line / component</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell align="right">Qty</TableCell>
                              <TableCell>Age</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {item.holds.map((hold) => (
                              <TableRow key={hold.id}>
                                {/* POS-ORDER-NUMBER.1 — a hold is what someone goes
                                    looking for on the POS, so show the number the
                                    POS shows them. The ObjectId stays in the
                                    tooltip for matching a record exactly, and is
                                    the fallback for holds placed before the sender
                                    included a number.

                                    The number is prose and is set as prose; a raw
                                    ObjectId keeps the monospace face that makes it
                                    comparable character by character. */}
                                <TableCell
                                  sx={hold.posNumber
                                    ? { fontSize: '0.85rem' }
                                    : { fontFamily: 'monospace', fontSize: '0.85rem' }}
                                >
                                  <Tooltip title={hold.posNumber ? `Order ${hold.posNumber} · ${hold.posRef}` : hold.posRef}>
                                    <span>{hold.posNumber ?? hold.posRef}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell>
                                  {hold.lineRef}
                                  {hold.componentIndex !== null && ` · C${hold.componentIndex}`}
                                </TableCell>
                                <TableCell>{hold.locationName}</TableCell>
                                <TableCell align="right">{formatQty(hold.qty)}</TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: getSeverityColor(hold.age.severity),
                                      fontWeight: hold.age.severity === 'red' ? 600 : 500,
                                    }}
                                  >
                                    {hold.age.formatted}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ReservedItemsCard;
